import { fetchWithAuth, getCSRFToken } from '../auth';
import { deleteTempBlob } from '../../services/azureBlobService';
import { executeAzureUploadFlow } from '../../utils/azureUploadFlow';
import {
    isAttachmentUploadEnabled,
    pendingAttachmentFile
} from './assetProblemAttachmentConfig';

const API_HOST = process.env.REACT_APP_ICC_TOOL_API_HOST;
const ASSET_PROBLEM_VIDEO_PREFIX = 'asset-problems/videos/';
const ASSET_PROBLEM_THUMBNAIL_PREFIX = 'asset-problems/thumbnails/';
const ASSET_PROBLEM_PICTURE_PREFIX = 'asset-problems/pictures/';
const ASSET_PROBLEM_QUOTE_DOCUMENT_PREFIX = 'asset-problems/docs/';

function attachmentsEndpoint (assetId, problemId) {
    return `${API_HOST}assets/${assetId}/problems/${problemId}/attachments/`;
}

function attachmentDetailEndpoint (assetId, problemId, attachmentId) {
    return `${attachmentsEndpoint(assetId, problemId)}${attachmentId}/`;
}

function attachmentPrefix (attachmentType) {
    if (attachmentType === 'picture') {
        return ASSET_PROBLEM_PICTURE_PREFIX;
    }
    if (attachmentType === 'quote_document') {
        return ASSET_PROBLEM_QUOTE_DOCUMENT_PREFIX;
    }
    if (attachmentType === 'video') {
        return ASSET_PROBLEM_VIDEO_PREFIX;
    }
    throw new Error(`Unsupported attachment type: ${attachmentType}`);
}

async function registerAttachment (assetId, problemId, payload) {
    const cleanupTempBlobs = () => {
        const blobName = payload.blobName;
        const thumbnailBlobName = payload.thumbnailBlobName;
        deleteTempBlob(blobName).catch(() => {
            console.warn('Failed to delete temporary blob', blobName);
        });
        if (thumbnailBlobName) deleteTempBlob(thumbnailBlobName).catch(() => {
            console.warn('Failed to delete temporary thumbnail blob', thumbnailBlobName);
        });
    };

    const response = await fetchWithAuth(attachmentsEndpoint(assetId, problemId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getCSRFToken() },
        credentials: 'include',
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        cleanupTempBlobs();
    }

    return response;
}

export async function uploadAttachment (assetId, problemId, attachmentType, file, options = {}) {
    const isVideo = attachmentType === 'video';

    const { registerResult } = await executeAzureUploadFlow({
        file,
        prefix: attachmentPrefix(attachmentType),
        thumbPrefix: isVideo ? ASSET_PROBLEM_THUMBNAIL_PREFIX : undefined,
        shouldExtractThumbnail: isVideo,
        onProgress: options.onProgress,
        onRegister: ({ blobName, thumbnailBlobName }) => registerAttachment(assetId, problemId, {
            attachmentType,
            blobName,
            thumbnailBlobName,
            originalFilename: file.name,
            fileSizeBytes: file.size
        })
    });

    return registerResult;
}

export async function deleteAttachment (assetId, problemId, attachmentId) {
    return fetchWithAuth(attachmentDetailEndpoint(assetId, problemId, attachmentId), {
        method: 'DELETE',
        headers: { 'X-CSRFToken': getCSRFToken() },
        credentials: 'include'
    });
}

export async function uploadPendingAttachments (assetId, problemId, pendingAttachments, options = {}) {
    const uploadConfigs = [];
    let totalBytes = 0;

    Object.entries(pendingAttachments).forEach(([attachmentType, files]) => {
        if (!isAttachmentUploadEnabled(attachmentType)) {
            return;
        }
        files.forEach(item => {
            const file = pendingAttachmentFile(item);
            const fileSizeBytes = file?.size || 0;
            totalBytes += fileSizeBytes;
            uploadConfigs.push({ attachmentType, file, fileSizeBytes });
        });
    });

    if (uploadConfigs.length === 0) {
        return { ok: true, uploaded: [] };
    }

    const fileProgress = new Array(uploadConfigs.length).fill(0);

    const emitProgress = () => {
        if (!options.onProgress) {
            return;
        }

        if (totalBytes > 0) {
            const uploadedBytes = uploadConfigs.reduce((sum, { fileSizeBytes }, index) => {
                return sum + (fileSizeBytes * ((fileProgress[index] ?? 0) / 100));
            }, 0);
            options.onProgress(Math.round((uploadedBytes / totalBytes) * 100));
            return;
        }

        const totalProgress = fileProgress.reduce((sum, progress) => sum + (progress ?? 0), 0);
        options.onProgress(Math.round(totalProgress / uploadConfigs.length));
    };

    emitProgress();

    let registrationChain = Promise.resolve();

    const uploadPromises = uploadConfigs.map(({ attachmentType, file }, i) =>
        executeAzureUploadFlow({
            file,
            prefix: attachmentPrefix(attachmentType),
            thumbPrefix: attachmentType === 'video' ? ASSET_PROBLEM_THUMBNAIL_PREFIX : undefined,
            shouldExtractThumbnail: attachmentType === 'video',
            onProgress: (progress) => {
                fileProgress[i] = progress;
                emitProgress();
            },
            onRegister: ({ blobName, thumbnailBlobName }) =>
                new Promise((resolve, reject) => {
                    registrationChain = registrationChain.then(() =>
                        registerAttachment(assetId, problemId, {
                            attachmentType,
                            blobName,
                            thumbnailBlobName,
                            originalFilename: file.name,
                            fileSizeBytes: file.size
                        }).then(resolve, reject)
                    );
                })
        })
    );

    const results = await Promise.allSettled(uploadPromises);
    const uploadedResponses = [];
    const failedTypes = new Set();

    results.forEach((result, index) => {
        const { attachmentType } = uploadConfigs[index];
        if (result.status === 'rejected') {
            failedTypes.add(attachmentType);
            return;
        }
        const { registerResult } = result.value;
        if (!registerResult.ok) {
            failedTypes.add(attachmentType);
            return;
        }
        uploadedResponses.push(registerResult);
    });

    if (options.onProgress) {
        options.onProgress(100);
    }

    if (failedTypes.size > 0) {
        return { ok: false, failedTypes: [...failedTypes] };
    }

    const uploaded = [];
    for (const response of uploadedResponses) {
        uploaded.push(await response.json());
    }

    return { ok: true, uploaded };
}
